import React from 'react';
import { Box, FormControl, FormLabel, Input, Button, Typography } from '@mui/joy';
import VendorSelect from './VendorSelect';
import SizeSelect from './SizeSelect';
import { today } from './MaterialForm';
import ExpiryDateShortcuts from './ExpiryDateShortcuts';
import { inputSx, submitButtonSx } from './formCommon';

export const emptyImplantForm = () => ({
  implant_name: '',
  date_entered: today(),
  expiry_date: '',
  quantity: '',
  rate: '',
  size_id: '',
  vendor_id: ''
});

export const validateImplantForm = (form) => {
  if (!form.implant_name?.trim() || !form.date_entered || !form.quantity || !form.size_id || !form.vendor_id) {
    return 'All required fields (name, date, quantity, size, vendor) must be filled';
  }
  if (form.vendor_id === '__add_new__') {
    return 'Please select a vendor or add one first';
  }
  if (parseInt(form.quantity, 10) <= 0) {
    return 'Quantity must be greater than 0';
  }
  return null;
};

export const implantFormToPayload = (form) => ({
  implant_name: form.implant_name,
  date_entered: form.date_entered,
  expiry_date: form.expiry_date || null,
  quantity: parseInt(form.quantity, 10),
  rate: form.rate !== '' ? form.rate : null,
  size_id: form.size_id || null,
  vendor_id: parseInt(form.vendor_id, 10)
});

const ImplantForm = ({
  form,
  onChange,
  onSubmit,
  loading = false,
  vendors = [],
  vendorsLoading = false,
  onAddVendor,
  submitLabel = 'Add Implant',
  fullWidthSubmit = true
}) => (
  <form onSubmit={onSubmit}>
    <FormControl sx={{ mb: 2 }}>
      <FormLabel>Implant Name</FormLabel>
      <Input
        name="implant_name"
        value={form.implant_name}
        onChange={onChange}
        required
        placeholder="Enter implant name"
        sx={inputSx}
      />
    </FormControl>

    <FormControl sx={{ mb: 1 }}>
      <FormLabel>Purchase Date</FormLabel>
      <Input type="date" name="date_entered" value={form.date_entered} onChange={onChange} required sx={inputSx} />
    </FormControl>

    <ExpiryDateShortcuts
      baseDate={form.date_entered}
      onExpiryChange={(expiry) => onChange({ target: { name: 'expiry_date', value: expiry } })}
      hintLabel="Set expiry from purchase date"
    />

    <FormControl sx={{ mb: 2 }}>
      <FormLabel>Expiry Date</FormLabel>
      <Input type="date" name="expiry_date" value={form.expiry_date} onChange={onChange} sx={inputSx} />
    </FormControl>

    <FormControl sx={{ mb: 2 }}>
      <FormLabel>Quantity</FormLabel>
      <Input
        type="number"
        name="quantity"
        value={form.quantity}
        onChange={onChange}
        required
        min="1"
        placeholder="Enter quantity"
        sx={inputSx}
      />
    </FormControl>

    <FormControl sx={{ mb: 2 }}>
      <FormLabel>Rate (per item)</FormLabel>
      <Input
        type="number"
        name="rate"
        value={form.rate}
        onChange={onChange}
        min="0"
        step="0.01"
        inputMode="decimal"
        placeholder="0.00"
        startDecorator={<Typography sx={{ color: 'text.tertiary', fontWeight: 500 }}>₹</Typography>}
        slotProps={{ input: { component: 'input' } }}
        sx={inputSx}
      />
    </FormControl>

    <SizeSelect
      value={form.size_id}
      onChange={(val) => onChange({ target: { name: 'size_id', value: val } })}
      label="Size"
      required
    />

    <VendorSelect
      vendors={vendors}
      value={form.vendor_id || ''}
      onChange={(vendorId) => onChange({ target: { name: 'vendor_id', value: vendorId } })}
      onAddNew={onAddVendor}
      loading={vendorsLoading}
      required
    />

    <Box sx={{ display: 'flex', justifyContent: fullWidthSubmit ? 'stretch' : 'flex-end', mt: 1 }}>
      <Button type="submit" loading={loading} fullWidth={fullWidthSubmit} sx={submitButtonSx}>
        {submitLabel}
      </Button>
    </Box>
  </form>
);

export default ImplantForm;
